
import { Component, Vue, Watch } from 'vue-property-decorator'
import HeadLable from '@/components/HeadLable/index.vue'
import Empty from '@/components/Empty/index.vue'
import ImageUpload from '@/components/ImgUpload/index.vue'
import {
  getWaterVoucherConfigList,
  addWaterVoucherConfig,
  updateWaterVoucherConfig,
  deleteWaterVoucherConfig,
  setWaterVoucherConfigEnabled,
  waterVoucherConfigSetSort
} from '@/api/waterVoucher'
import { getCategoryList } from '@/api/category'
import { queryDishList } from '@/api/dish'
import { listMyStores } from '@/api/store'
import { UserModule } from '@/store/modules/user'
import SortInlineEdit from '@/components/SortInlineEdit/index.vue'

@Component({
  name: 'WaterVoucherConfig',
  components: { HeadLable, Empty, ImageUpload, SortInlineEdit }
})
export default class extends Vue {
  private tableData: any[] = []
  private listLoading = false
  private dialogVisible = false
  private dialogTitle = '新增套餐'
  private formKey = 0
  private imageUrl = ''
  private storeOptions: Array<{ id: number; name: string }> = []
  private categoryOptions: Array<{ id: number; name: string; label: string }> = []
  private brandCategoryOptions: Array<{ id: number; name: string; label: string }> = []
  private dishOptions: Array<{
    id: number
    name: string
    image: string
    price: number
    categoryId: number | null
    displayCategoryIds: number[]
  }> = []
  /** 分类 → 商品级联树（叶子为商品 id） */
  private dishCascaderOptions: any[] = []
  private dishCascaderProps = {
    value: 'value',
    label: 'label',
    children: 'children',
    emitPath: false,
    expandTrigger: 'hover' as const
  }
  private dishNameMap: Record<string, string> = {}
  private allCategoryFlat: any[] = []
  private categoryNameMap: Record<string, string> = {}
  private categoryLoading = false
  private dishLoading = false
  /** 用户是否手动上传过封面（有则优先，否则回退商品图） */
  private coverManual = false
  private form: any = this.createEmptyForm()

  get selectedDishImage() {
    const dish = this.dishOptions.find(
      (d) => d.id != null && Number(d.id) === Number(this.form.dishId)
    )
    return (dish && dish.image) || ''
  }

  get coverPreviewUrl() {
    if (this.coverManual) {
      return this.imageUrl || ''
    }
    return this.selectedDishImage
  }

  /** 强制上传组件在商品/封面源变化时重挂载，避免回显不同步 */
  get coverUploadKey() {
    return `${this.formKey}-${this.form.dishId || 0}-${this.coverManual ? 'm' : 'd'}-${this.coverPreviewUrl || ''}`
  }

  created() {
    this.loadStores()
    this.loadList()
  }

  @Watch('form.storeId')
  private onFormStoreIdChange() {
    if (this.dialogVisible) {
      this.loadDishOptions(true)
    }
  }

  private createEmptyForm() {
    return {
      id: null,
      storeId: 0 as number | null,
      name: '',
      description: '',
      image: '',
      buyBuckets: '',
      giftBuckets: '',
      salePrice: '',
      inviterRewardAmount: '',
      bucketCategoryId: null as number | null,
      brandCategoryId: null as number | null,
      dishId: null as number | null,
      sort: '0',
      enabledSwitch: false
    }
  }

  private onCoverChange(url: string) {
    this.imageUrl = url || ''
    this.form.image = this.imageUrl
    this.coverManual = !!this.imageUrl
  }

  private onDishChange() {
    // 切换对应商品后，封面跟随新商品图（可再手动上传覆盖）
    this.resetCoverToDishDefault()
    this.applySalePriceFromDish()
  }

  private onDishSelectVisible(visible: boolean) {
    if (visible) {
      this.loadDishOptions(false)
    }
  }

  private onBuyBucketsChange() {
    this.applySalePriceFromDish()
  }

  /** 售价 = 商品单价 × 每份购买桶数 */
  private applySalePriceFromDish() {
    if (this.form.dishId == null || this.form.dishId === '') {
      return
    }
    const dish = this.dishOptions.find(
      (d) => d.id != null && Number(d.id) === Number(this.form.dishId)
    )
    if (!dish || dish.price == null || !Number.isFinite(Number(dish.price))) {
      return
    }
    if (!String(this.form.buyBuckets || '').trim()) {
      this.form.buyBuckets = '10'
    }
    const buckets = Number(this.form.buyBuckets)
    if (!Number.isFinite(buckets) || buckets <= 0) {
      return
    }
    const unit = Number(dish.price)
    const sale = Math.round(unit * buckets * 100) / 100
    this.form.salePrice = String(sale)
  }

  private resetCoverToDishDefault() {
    this.coverManual = false
    this.imageUrl = ''
    this.form.image = ''
  }

  private formatDishLabel(row: any) {
    if (!row) return '—'
    if (row.dishName) return String(row.dishName)
    if (row.dishId != null && this.dishNameMap[String(row.dishId)]) {
      return this.dishNameMap[String(row.dishId)]
    }
    return row.dishId != null ? `商品#${row.dishId}` : '—'
  }

  private getCurrentStoreId(): number | null {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  private get currentStoreLabel(): string {
    const sid = this.getCurrentStoreId()
    if (sid == null) return '未选择商户'
    const found = this.storeOptions.find((s) => Number(s.id) === sid)
    return found ? found.name : `店铺#${sid}`
  }

  /** 仅展示当前商户配置（隐藏历史全局行） */
  private onlyCurrentStoreRows(list: any[]): any[] {
    const sid = this.getCurrentStoreId()
    if (sid == null) return []
    return (list || []).filter(
      (row) => row && row.storeId != null && Number(row.storeId) === sid
    )
  }

  private async loadStores() {
    try {
      const res: any = await listMyStores()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.storeOptions = res.data.data.map((it: any) => ({
          id: it.id,
          name: it.storeName || it.name || `店铺${it.id}`
        }))
        await this.loadCategoryNameMapForTable()
      } else {
        this.storeOptions = []
      }
    } catch (e) {
      this.storeOptions = []
    }
  }

  /** 列表展示：预加载各店铺分类全路径名称 */
  private async loadCategoryNameMapForTable() {
    const map: Record<string, string> = { ...this.categoryNameMap }
    for (const s of this.storeOptions) {
      try {
        const res: any = await getCategoryList({ storeId: s.id, type: 1 })
        if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
          const flat = res.data.data
          flat.forEach((c: any) => {
            if (c && c.id != null) {
              const parts: string[] = []
              const byId: Record<string, any> = {}
              flat.forEach((x: any) => {
                if (x && x.id != null) byId[String(x.id)] = x
              })
              let cur = c
              let guard = 0
              while (cur && guard < 8) {
                parts.unshift(cur.name)
                const pid =
                  cur.parentId != null && cur.parentId !== 0
                    ? byId[String(cur.parentId)]
                    : null
                cur = pid
                guard++
              }
              map[String(c.id)] = parts.join(' / ')
            }
          })
        }
      } catch (e) {
        // ignore single store failure
      }
    }
    this.categoryNameMap = map
  }

  private getCategoryParentId(item: any) {
    if (!item) return null
    const p = item.parentId != null ? item.parentId : item.parent_id
    if (p == null || p === '' || Number(p) === 0) return null
    return Number(p)
  }

  /** 分类层级：1 一级 / 2 二级 / 3 三级 */
  private getCategoryDepth(categoryId: number | string, list?: any[]) {
    const pool = list || this.allCategoryFlat
    const map: Record<string, any> = {}
    pool.forEach((c: any) => {
      if (c && c.id != null) map[String(c.id)] = c
    })
    let depth = 0
    let cur = map[String(categoryId)]
    let guard = 0
    while (cur && guard < 8) {
      depth += 1
      const pid = this.getCategoryParentId(cur)
      cur = pid != null ? map[String(pid)] : null
      guard++
    }
    return depth
  }

  private buildCategoryPath(categoryId: number | string, list?: any[]) {
    const pool = list || this.allCategoryFlat
    const map: Record<string, any> = {}
    pool.forEach((c: any) => {
      if (c && c.id != null) map[String(c.id)] = c
    })
    const parts: string[] = []
    let cur = map[String(categoryId)]
    let guard = 0
    while (cur && guard < 8) {
      parts.unshift(cur.name)
      const pid = this.getCategoryParentId(cur)
      cur = pid != null ? map[String(pid)] : null
      guard++
    }
    return parts.join(' / ') || `分类#${categoryId}`
  }

  /**
   * 计桶分类应为二级子分类（如一级「饮用水」下的「桶装水」），不含同级兄弟分类
   */
  private buildChildCategoryOptions(list: any[]) {
    if (!Array.isArray(list)) {
      return []
    }
    const roots = list.filter(
      (c) =>
        c &&
        c.id != null &&
        (c.parentId == null || c.parentId === 0) &&
        (c.type == null || Number(c.type) === 1)
    )
    const rootMap: Record<string, string> = {}
    roots.forEach((r: any) => {
      rootMap[String(r.id)] = r.name
    })
    return list
      .filter(
        (c) =>
          c &&
          c.id != null &&
          c.parentId != null &&
          c.parentId !== 0 &&
          (c.type == null || Number(c.type) === 1)
      )
      .map((c: any) => {
        const parentName = rootMap[String(c.parentId)] || '上级'
        const label = `${parentName} / ${c.name}`
        return { id: c.id, name: c.name, label }
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'))
  }

  private resolveCategoryStoreId(): number | null {
    return this.getCurrentStoreId()
  }

  private async loadAllCategoriesForStore(storeId: number) {
    const res: any = await getCategoryList({ storeId, type: 1 })
    if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
      this.allCategoryFlat = res.data.data
      this.allCategoryFlat.forEach((c: any) => {
        if (c && c.id != null) {
          this.categoryNameMap[String(c.id)] = this.buildCategoryPath(c.id)
        }
      })
      return true
    }
    this.allCategoryFlat = []
    return false
  }

  private async loadCategoryOptions(resetIfInvalid = false) {
    const storeId = this.resolveCategoryStoreId()
    if (!storeId) {
      this.categoryOptions = []
      return
    }
    this.categoryLoading = true
    try {
      if (!(await this.loadAllCategoriesForStore(storeId))) {
        this.categoryOptions = []
        return
      }
      this.categoryOptions = this.buildChildCategoryOptions(this.allCategoryFlat)
      this.categoryOptions.forEach((c) => {
        this.categoryNameMap[String(c.id)] = c.label
      })
      if (resetIfInvalid && this.form.bucketCategoryId != null) {
        const exists = this.categoryOptions.some((c) => c.id === this.form.bucketCategoryId)
        if (!exists) {
          this.form.bucketCategoryId = null
        }
      }
    } catch (e) {
      this.categoryOptions = []
    } finally {
      this.categoryLoading = false
    }
  }

  private async loadBrandCategoryOptions(resetIfInvalid = false) {
    const storeId = this.resolveCategoryStoreId()
    if (!storeId) {
      this.brandCategoryOptions = []
      return
    }
    this.categoryLoading = true
    try {
      const leafRes: any = await getCategoryList({ storeId, type: 1, leafOnly: true })
      if (!(await this.loadAllCategoriesForStore(storeId))) {
        this.brandCategoryOptions = []
        return
      }
      const leaves =
        String(leafRes.data.code) === '1' && Array.isArray(leafRes.data.data)
          ? leafRes.data.data
          : []
      this.brandCategoryOptions = leaves
        .filter((c: any) => Number(c.status) === 1)
        .filter((c: any) => this.getCategoryDepth(c.id) === 3)
        .map((c: any) => ({
          id: c.id,
          name: c.name,
          label: this.buildCategoryPath(c.id)
        }))
      if (!this.brandCategoryOptions.length && leaves.length) {
        const hasEnabledLeaf = leaves.some((c: any) => Number(c.status) === 1)
        this.$message.warning(
          hasEnabledLeaf
            ? '暂无启用的三级品牌分类，请先在分类管理中创建三级分类后再绑定'
            : '品牌分类已创建但未启用，请在分类管理中启用后再绑定'
        )
      }
      if (resetIfInvalid && this.form.brandCategoryId != null) {
        const exists = this.brandCategoryOptions.some(
          (c) => c.id === this.form.brandCategoryId
        )
        if (!exists) {
          this.form.brandCategoryId = null
          this.form.dishId = null
          this.dishOptions = []
        }
      }
    } catch (e) {
      this.brandCategoryOptions = []
    } finally {
      this.categoryLoading = false
    }
  }

  /** 构建「展示分类树 → 商品叶子」；挂载 dish_category，不用 dish.category_id */
  private rebuildDishCascaderTree() {
    const nodes: Record<string, any> = {}
    ;(this.allCategoryFlat || []).forEach((c: any) => {
      if (!c || c.id == null) return
      nodes[String(c.id)] = {
        value: `c-${c.id}`,
        label: c.name || `分类#${c.id}`,
        children: [] as any[],
        _parentId: this.getCategoryParentId(c)
      }
    })

    const uncategorizedDishes: any[] = []
    this.dishOptions.forEach((d) => {
      const leaf = {
        value: Number(d.id),
        label: d.name || `商品#${d.id}`
      }
      const ids = Array.isArray(d.displayCategoryIds) ? d.displayCategoryIds : []
      let mounted = false
      ids.forEach((cid) => {
        if (cid == null) return
        const key = String(cid)
        const node = nodes[key]
        if (!node) return
        if (!node.children.some((c: any) => Number(c.value) === leaf.value)) {
          node.children.push({ ...leaf })
        }
        mounted = true
      })
      if (!mounted) {
        uncategorizedDishes.push(leaf)
      }
    })

    const roots: any[] = []
    Object.keys(nodes).forEach((key) => {
      const node = nodes[key]
      const pid = node._parentId
      delete node._parentId
      if (pid != null && nodes[String(pid)]) {
        nodes[String(pid)].children.push(node)
      } else {
        roots.push(node)
      }
    })

    const sortAndPrune = (list: any[]): any[] => {
      const next = (list || [])
        .map((n) => {
          if (n == null) return null
          // 商品叶子：无 children
          if (typeof n.value === 'number') {
            return n
          }
          const kids = sortAndPrune(n.children || [])
          if (!kids.length) {
            return null
          }
          return { ...n, children: kids }
        })
        .filter(Boolean) as any[]
      next.sort((a, b) => {
        const aLeaf = typeof a.value === 'number' ? 1 : 0
        const bLeaf = typeof b.value === 'number' ? 1 : 0
        if (aLeaf !== bLeaf) return aLeaf - bLeaf
        return String(a.label).localeCompare(String(b.label), 'zh-CN')
      })
      return next
    }

    const tree = sortAndPrune(roots)
    if (uncategorizedDishes.length) {
      uncategorizedDishes.sort((a, b) =>
        String(a.label).localeCompare(String(b.label), 'zh-CN')
      )
      tree.push({
        value: 'c-uncategorized',
        label: '未分类',
        children: uncategorizedDishes
      })
    }
    this.dishCascaderOptions = tree
  }

  private async loadDishOptions(resetIfInvalid = false) {
    const storeId = this.resolveCategoryStoreId()
    if (!storeId) {
      this.dishOptions = []
      this.dishCascaderOptions = []
      if (resetIfInvalid) {
        this.form.dishId = null
      }
      return
    }
    this.dishLoading = true
    try {
      await this.loadAllCategoriesForStore(storeId)
      const res: any = await queryDishList({ storeId })
      const list =
        String(res.data.code) === '1' && Array.isArray(res.data.data) ? res.data.data : []
      this.dishOptions = list
        .filter((d: any) => d && d.id != null)
        .map((d: any) => ({
          id: Number(d.id),
          name: d.name || `商品#${d.id}`,
          image: d.image || '',
          price: d.price != null ? Number(d.price) : NaN,
          categoryId: d.categoryId != null ? Number(d.categoryId) : null,
          displayCategoryIds: Array.isArray(d.displayCategoryIds)
            ? d.displayCategoryIds
                .map((id: any) => Number(id))
                .filter((id: number) => !Number.isNaN(id))
            : []
        }))
      this.dishOptions.forEach((d) => {
        this.dishNameMap[String(d.id)] = d.name
      })
      this.rebuildDishCascaderTree()
      if (resetIfInvalid && this.form.dishId != null) {
        const exists = this.dishOptions.some((d) => d.id === Number(this.form.dishId))
        if (!exists) {
          this.form.dishId = null
        }
      }
      this.syncCoverManualWithDish()
      if (!this.form.id && this.form.dishId != null) {
        this.applySalePriceFromDish()
      }
    } catch (e) {
      this.dishOptions = []
      this.dishCascaderOptions = []
    } finally {
      this.dishLoading = false
    }
  }

  /** 封面若与商品图相同，视为未手动上传（预览走商品图，切换商品会跟着变） */
  private syncCoverManualWithDish() {
    if (!this.form.dishId) return
    const dish = this.dishOptions.find(
      (d) => d.id != null && Number(d.id) === Number(this.form.dishId)
    )
    if (!dish || !dish.image) return
    const current = this.imageUrl || this.form.image || ''
    if (current && current === dish.image) {
      this.resetCoverToDishDefault()
    }
  }

  private async enrichDishNameMap(rows: any[]) {
    const storeId = this.getCurrentStoreId()
    if (!storeId || !rows || !rows.length) return
    try {
      const res: any = await queryDishList({ storeId })
      const list =
        String(res.data.code) === '1' && Array.isArray(res.data.data) ? res.data.data : []
      list.forEach((d: any) => {
        if (d && d.id != null) {
          this.dishNameMap[String(d.id)] = d.name || `商品#${d.id}`
        }
      })
    } catch (e) {
      /* ignore */
    }
  }

  private onCategorySelectVisible(visible: boolean) {
    if (visible) {
      this.loadCategoryOptions(false)
    }
  }

  private onBrandCategorySelectVisible(visible: boolean) {
    if (visible) {
      this.loadBrandCategoryOptions(false)
    }
  }

  private formatBrandCategoryLabel(categoryId: number | null | undefined) {
    if (categoryId == null || categoryId === 0) {
      return '未绑定'
    }
    return this.categoryNameMap[String(categoryId)] || `分类#${categoryId}`
  }

  private formatBucketCategoryLabel(categoryId: number | null | undefined) {
    if (categoryId == null || categoryId === 0) {
      return '名称含「桶」'
    }
    const name = this.categoryNameMap[String(categoryId)]
    return name || `分类#${categoryId}`
  }

  private formatStoreLabel(storeId: number | null | undefined) {
    if (storeId == null || storeId === 0) {
      return '（历史全局，已隐藏）'
    }
    const found = this.storeOptions.find(s => s.id === storeId)
    return found ? found.name : `店铺${storeId}`
  }

  private async loadList(options?: { silent?: boolean }) {
    // 默认静默（保存/排序后重载）；点「刷新」传 silent: false
    const silent = !(options && options.silent === false)
    if (this.listLoading) return
    this.listLoading = true
    try {
      const res = await getWaterVoucherConfigList()
      if (String(res.data.code) === '1') {
        const rows = this.onlyCurrentStoreRows(res.data.data || [])
        rows.sort((a: any, b: any) => {
          const sa = Number(a.sort != null ? a.sort : 0)
          const sb = Number(b.sort != null ? b.sort : 0)
          if (sa !== sb) return sa - sb
          return Number(a.id) - Number(b.id)
        })
        this.tableData = rows
        await this.enrichDishNameMap(rows)
        if (!silent) {
          this.$message.success('已刷新')
        }
      } else if (!silent) {
        this.$message.error((res.data && (res.data.msg || res.data.desc)) || '刷新失败')
      }
    } catch (e) {
      console.log(e)
      if (!silent) {
        this.$message.error('刷新失败')
      }
    } finally {
      this.listLoading = false
    }
  }

  private refreshList() {
    this.loadList({ silent: false })
  }

  private setSortHandle(row: any, sort: number) {
    if (!row || !row.id) return
    waterVoucherConfigSetSort({ id: row.id, sort })
      .then((res: any) => {
        if (res && res.data && String(res.data.code) === '1') {
          this.$message.success('排序已更新')
          this.loadList()
        } else {
          this.$message.error((res.data && (res.data.msg || res.data.desc)) || '排序失败')
        }
      })
      .catch((err: any) => {
        this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
      })
  }

  private openDialog(row?: any) {
    this.formKey += 1
    if (row) {
      this.dialogTitle = '修改套餐'
      this.form = {
        id: row.id,
        storeId: row.storeId != null ? row.storeId : 0,
        name: row.name || '',
        description: row.description || '',
        image: row.image || '',
        buyBuckets: String(row.buyBuckets != null ? row.buyBuckets : ''),
        giftBuckets: String(row.giftBuckets != null ? row.giftBuckets : ''),
        salePrice: String(row.salePrice != null ? row.salePrice : ''),
        inviterRewardAmount: String(row.inviterRewardAmount != null ? row.inviterRewardAmount : ''),
        bucketCategoryId: row.bucketCategoryId != null ? Number(row.bucketCategoryId) : null,
        brandCategoryId: row.brandCategoryId != null ? Number(row.brandCategoryId) : null,
        dishId: row.dishId != null ? Number(row.dishId) : null,
        sort: String(row.sort != null ? row.sort : 0),
        enabledSwitch: String(row.enabled) === '1'
      }
      this.imageUrl = row.image || ''
      this.coverManual = !!this.imageUrl
    } else {
      this.dialogTitle = '新增套餐'
      this.form = this.createEmptyForm()
      this.imageUrl = ''
      this.coverManual = false
    }
    const currentStoreId = this.getCurrentStoreId()
    this.form.storeId = currentStoreId != null ? currentStoreId : null
    this.dialogVisible = true
    this.$nextTick(() => {
      this.loadDishOptions(false)
    })
  }

  private onDialogClosed() {
    this.form = this.createEmptyForm()
    this.imageUrl = ''
    this.coverManual = false
    this.dishOptions = []
    this.dishCascaderOptions = []
  }

  private async submit() {
    const storeId = this.getCurrentStoreId()
    if (!storeId) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    if (this.form.dishId == null || this.form.dishId === '') {
      this.$message.warning('请选择对应商品')
      return
    }
    const giftBuckets = Number(this.form.giftBuckets)
    // 未手动上传封面时传空，后端回退为商品图
    const image = this.coverManual ? this.imageUrl || '' : ''
    const selectedDish = this.dishOptions.find(
      (d) => d.id != null && Number(d.id) === Number(this.form.dishId)
    )
    const payload: any = {
      // 名称由后端按商品自动生成，前端不再录入
      name: '',
      description: this.form.description || '',
      image,
      buyBuckets: Number(this.form.buyBuckets),
      giftBuckets: Number.isFinite(giftBuckets) && giftBuckets >= 0 ? giftBuckets : 0,
      salePrice: Number(this.form.salePrice),
      inviterRewardAmount: 0,
      enabled: this.form.id ? (this.form.enabledSwitch ? 1 : 0) : 0,
      sort: this.form.id ? Number(this.form.sort || 0) : null,
      storeId,
      // 品牌由后端按商品分类回填；前端有则顺带传
      brandCategoryId:
        selectedDish && selectedDish.categoryId != null
          ? Number(selectedDish.categoryId)
          : this.form.brandCategoryId != null
            ? Number(this.form.brandCategoryId)
            : null,
      dishId: Number(this.form.dishId)
    }
    if (this.form.bucketCategoryId != null && this.form.bucketCategoryId !== '') {
      payload.bucketCategoryId = Number(this.form.bucketCategoryId)
    }
    try {
      if (this.form.id) {
        payload.id = this.form.id
        const res: any = await updateWaterVoucherConfig(payload)
        if (!res || !res.data || String(res.data.code) !== '1') {
          this.$message.error((res && res.data && (res.data.msg || res.data.desc)) || '保存失败')
          return
        }
      } else {
        const res: any = await addWaterVoucherConfig(payload)
        if (!res || !res.data || String(res.data.code) !== '1') {
          this.$message.error((res && res.data && (res.data.msg || res.data.desc)) || '保存失败')
          return
        }
      }
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.loadList()
    } catch (e) {
      const err: any = e
      const msg =
        (err && err.response && err.response.data && (err.response.data.msg || err.response.data.desc)) ||
        (err && err.message) ||
        '保存失败'
      this.$message.error(msg)
    }
  }

  private async toggleEnabled(row: any) {
    const enabled = String(row.enabled) === '1' ? 0 : 1
    try {
      await setWaterVoucherConfigEnabled(row.id, enabled)
      this.$message.success('操作成功')
      this.loadList()
    } catch (e) {
      this.$message.error('操作失败')
    }
  }

  private deleteRow(row: any) {
    if (!row || !row.id) return
    const name = this.formatDishLabel(row) || `套餐#${row.id}`
    this.$confirm(`确定删除「${name}」对应套餐？删除后不可恢复。`, '提示', {
      type: 'warning'
    })
      .then(async () => {
        try {
          await deleteWaterVoucherConfig(row.id)
          this.$message.success('已删除')
          this.loadList()
        } catch (e) {
          this.$message.error('删除失败')
        }
      })
      .catch(() => {})
  }
}
